import type { BasicQuestion } from "@repo/app-types/client/client";
import type { InterviewLanguage } from "@repo/app-types/constants/languages";
import {
  type Assessment,
  getErrorMessageForAssessmentCriteriaValue,
  getHaveAssessmentFieldsChanged,
  sortAssessmentFields,
} from "@repo/app-types/entities/assessment";
import type {
  CustomFieldDefinition,
  CustomFieldDefinitionInsertClient,
} from "@repo/app-types/entities/customField";
import type { ProjectUpdate } from "@repo/app-types/entities/project";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { Size } from "@repo/ui/helpers/helpers";
import { useConfirmationModal } from "@repo/ui/hooks/useConfirmationModal";
import { Label } from "@repo/ui/Label/label";
import { Slideover } from "@repo/ui/Slideover/slideover";
import { type FC, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { useProjectJobDescriptionFeedbackByIdQuery } from "../../api/entities/projectFeedback.js";
import { useIsFeatureFlagEnabled } from "../../helpers/featureFlags/hooks.js";
import { Can } from "../../helpers/teammateAuthorizationContext.js";
import { useAppDispatch } from "../../hooks/hook.js";
import { AssessmentSettings } from "../assessments/assessmentSettings.js";
import { updateAssessment } from "../assessments/assessmentsSlice.js";
import { InterviewSectionDefinitionListPanel } from "../interviewSections/definitions/listPanel.js";
import { useInterviewSectionDefinitionsStore } from "../interviewSections/definitions/store.js";
import { SuggestionsWidget } from "../projects/feedback/suggestionsWidget.js";
import { JobDescriptionTextArea } from "../projects/projectSettings/jobDescriptionSetting.js";
import { QuestionsPanel } from "../projects/recruitingQuestions/questionsPanel.js";
import {
  questionsActions,
  useQuestionsStore,
} from "../projects/recruitingQuestions/questionsStore.js";

interface JobTypeEditorSlideoverProps {
  projectId: string;
  jobTypeId: string | undefined;
  basicQuestions: BasicQuestion[];
  assessment: Assessment | null;
  language: InterviewLanguage;
  onSaveProject: (changes: ProjectUpdate) => void;
  isOpen: boolean;
  onClose: () => void;
  jobDescription: string | null;
}

export const JobTypeEditorSlideover: FC<JobTypeEditorSlideoverProps> = (
  props,
) => {
  const { projectId, assessment, language, onSaveProject, isOpen, onClose } =
    props;
  const dispatch = useAppDispatch();

  const isInterviewSectionsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.InterviewSections,
  );

  const sectionsJobTypeId = isInterviewSectionsEnabled
    ? props.jobTypeId
    : undefined;

  const [jobDescription, setJobDescription] = useState(
    props.jobDescription ?? "",
  );
  // State for managing basic questions
  const [
    { questions, hasMadeChanges: hasMadeChangesToQuestions },
    onQuestionsChange,
  ] = useQuestionsStore(props.basicQuestions);

  const {
    changedSections,
    onSectionCreate,
    onSectionUpdate,
    onSectionDelete,
    onSectionsSave,
    onSectionsReset,
    hasMadeChanges: hasMadeChangesToSections,
  } = useInterviewSectionDefinitionsStore(sectionsJobTypeId);

  const [hasMadeChangesHere, setHasMadeChanges] = useState(false);
  const hasMadeChanges =
    hasMadeChangesHere || hasMadeChangesToQuestions || hasMadeChangesToSections;

  // State for managing assessment criteria
  const sortedOriginalFields = sortAssessmentFields(
    assessment?.custom_field_definitions ?? [],
  );
  const [assessmentFields, setAssessmentFields] =
    useState<(CustomFieldDefinition | CustomFieldDefinitionInsertClient)[]>(
      sortedOriginalFields,
    );
  const [assessmentFieldErrors, setAssessmentFieldErrors] = useState<string[]>(
    [],
  );

  const { renderConfirmationModal, onConfirmThenClose } = useConfirmationModal({
    needsConfirmation: hasMadeChanges,
    onClose,
  });

  const jobDescriptionFeedback =
    useProjectJobDescriptionFeedbackByIdQuery(projectId);

  // Reset state when slideover is opened/closed
  useEffect(() => {
    onQuestionsChange(questionsActions.initialized(props.basicQuestions));
    setAssessmentFields(sortedOriginalFields);
    setAssessmentFieldErrors([]);
    setHasMadeChanges(false);
    setJobDescription(props.jobDescription ?? "");
    onSectionsReset();
  }, [isOpen]);

  const validateAssessmentFields = (
    fieldsToValidate: (
      | CustomFieldDefinition
      | CustomFieldDefinitionInsertClient
    )[],
  ) => {
    const newErrors = fieldsToValidate.map((field) =>
      getErrorMessageForAssessmentCriteriaValue(field.display_name),
    );
    setAssessmentFieldErrors(newErrors);
    return newErrors.every((error) => error === "");
  };

  const onSave = () => {
    const nonEmptyFields = assessmentFields.filter(
      (field) => field.display_name.trim() !== "",
    );

    if (validateAssessmentFields(nonEmptyFields)) {
      const shouldUpdateAssessment =
        assessment &&
        getHaveAssessmentFieldsChanged(sortedOriginalFields, nonEmptyFields);

      if (shouldUpdateAssessment) {
        dispatch(
          updateAssessment({
            assessmentId: assessment.id,
            changes: {
              custom_field_definitions: nonEmptyFields,
            },
          }),
        );
      }

      onSectionsSave();

      setHasMadeChanges(false);

      onSaveProject({
        questions: questions
          .filter((q) => q.question)
          .map((q) => ({
            question: q.question,
          })), // Filter out empty questions and remove the temporary IDs
        job_description: jobDescription,
      });
      onClose();
    }
  };

  return (
    <>
      {renderConfirmationModal()}
      <Slideover
        buttons={
          <>
            <Can
              I={PermissionActions.UPDATE}
              a={PermissionSubjects.JOB_OPENINGS}
              passThrough={true}
            >
              {(allowed) => (
                <Button
                  isDisabled={!allowed || !hasMadeChanges}
                  onClick={() => {
                    onSave();
                  }}
                  size={Size.MEDIUM}
                  variant={ButtonVariant.Primary}
                >
                  Save changes
                </Button>
              )}
            </Can>
            <Button
              onClick={onClose}
              size={Size.MEDIUM}
              variant={ButtonVariant.Secondary}
            >
              Discard changes
            </Button>
          </>
        }
        onClose={onConfirmThenClose}
        shouldShow={isOpen}
        title="Interview editor"
      >
        <div className="flex h-full flex-row bg-background">
          <div className="flex w-[500px] flex-col border-r border-border-light">
            <div
              className={twMerge(
                "space-between flex flex-col bg-background px-3 py-2",
                // The new right-side panel does not have a border under the header.
                !sectionsJobTypeId ? "border-b border-border-light" : "",
              )}
            >
              <Label size={Size.MEDIUM}>Grading</Label>
              <div className="text-xs text-gray-600">
                Interviews will be scored based on the JD and job
                qualifications.
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              <div className="space-between flex flex-col px-3 py-2">
                <JobDescriptionTextArea
                  labelAccessory={
                    <SuggestionsWidget
                      isLoading={jobDescriptionFeedback.isLoading}
                      isFetching={jobDescriptionFeedback.isFetching}
                      summary={jobDescriptionFeedback.data?.overall_feedback}
                      suggestions={jobDescriptionFeedback.data?.suggestions.map(
                        (suggestion) => suggestion.suggestion,
                      )}
                      onRefreshClick={jobDescriptionFeedback.refetch}
                    />
                  }
                  value={jobDescription}
                  onChange={(newValue) => {
                    setHasMadeChanges(true);
                    setJobDescription(newValue);
                  }}
                  description={"A detailed description of company and role."}
                  height="200px"
                />
              </div>
              <AssessmentSettings
                language={language}
                assessment={assessment}
                fields={assessmentFields}
                setFields={(fields) => {
                  setHasMadeChanges(true);
                  setAssessmentFields(fields);
                }}
                fieldErrors={assessmentFieldErrors}
                setFieldErrors={setAssessmentFieldErrors}
              />
            </div>
          </div>
          {sectionsJobTypeId ? (
            <InterviewSectionDefinitionListPanel
              projectId={props.projectId}
              jobTypeId={sectionsJobTypeId}
              changedSections={changedSections}
              onSectionCreate={onSectionCreate}
              onSectionUpdate={onSectionUpdate}
              onSectionDelete={onSectionDelete}
              questions={questions}
              onQuestionsChange={onQuestionsChange}
            />
          ) : (
            <QuestionsPanel
              projectId={props.projectId}
              questions={questions}
              onQuestionsChange={onQuestionsChange}
            />
          )}
        </div>
      </Slideover>
    </>
  );
};
